import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Fade,
  Zoom,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import TapAndPlayIcon from '@mui/icons-material/TapAndPlay';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import SpeedIcon from '@mui/icons-material/Speed';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const ProductDebit = () => {
  // Banking Features - 4 cards
  const bankingFeatures = [
    { icon: <AccountBalanceIcon sx={{ fontSize: 24 }} />, title: "Online Banking", desc: "Real time balances, statements, manage debit orders, make payments" },
    { icon: <NotificationsIcon sx={{ fontSize: 24 }} />, title: "Transaction Notifications", desc: "SMS notifications for incoming and outgoing payments" },
    { icon: <PaymentIcon sx={{ fontSize: 24 }} />, title: "Easy Deposits & Withdrawals", desc: "Deposit/withdraw at retail stores or via PayPal" },
    { icon: <TapAndPlayIcon sx={{ fontSize: 24 }} />, title: "Tap and Go", desc: "Easy payment at participating retailers" },
  ];

  // Money Management Features - 4 cards
  const moneyManagementFeatures = [
    { icon: <ShoppingCartIcon sx={{ fontSize: 24 }} />, title: "No Cash Needed", desc: "Take control of your spending effortlessly" },
    { icon: <AttachMoneyIcon sx={{ fontSize: 24 }} />, title: "No Fixed Banking Fees", desc: "Only $50 one-time fee, 0.5% transaction fee" },
    { icon: <SecurityIcon sx={{ fontSize: 24 }} />, title: "Purchase Protection", desc: "PIN required, protection on online purchases" },
    { icon: <PhoneAndroidIcon sx={{ fontSize: 24 }} />, title: "Mobile App", desc: "Check balance, view transactions, pay other holders" },
  ];

  const steps = ['Complete Application', 'No Credit Check', 'Instant Approval', 'Express Delivery'];

  return (
    <Box>
      {/* Hero Section with Product Page Background Image - No Logo */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 10, md: 14 }, 
          minHeight: { xs: 450, md: 700},
          color: 'white', 
          textAlign: 'center',
          backgroundImage: 'url("/images/productspageherobg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '4rem' }, 
                  mb: 2, 
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.premiumGold} 0%, ${colors.lightGold} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: -0.5,
                }}
              >
                DIAMOND CARD
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '3rem' }, 
                  color: colors.premiumGold, 
                  mb: 1, 
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                Debit Cards
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Banking Features Section - 4 cards in one row */}
      <Container sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip 
            label="BANKING FEATURES" 
            sx={{ 
              bgcolor: colors.premiumGold, 
              color: colors.navy, 
              fontWeight: 700,
              px: 2,
              py: 1,
              fontSize: '0.75rem',
              letterSpacing: 1,
              mb: 2,
            }}
          />
          <Typography 
            variant="h4" 
            textAlign="center" 
            sx={{ 
              fontWeight: 700, 
              color: colors.navy, 
              fontSize: { xs: '1.5rem', md: '1.8rem' }
            }}
          >
            Seamless Banking Experience
          </Typography>
        </Box>
        
        <Grid container spacing={2.5} alignItems="stretch">
          {bankingFeatures.map((feature, idx) => (
            <Zoom in timeout={300 + idx * 100} key={idx}>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    borderTop: `2px solid ${colors.premiumGold}`,
                    '&:hover': { 
                      transform: 'translateY(-4px)', 
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      '& .feature-icon': {
                        transform: 'scale(1.05)',
                        backgroundColor: colors.premiumGold,
                      }
                    } 
                  }}
                >
                  <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Avatar 
                      className="feature-icon"
                      sx={{ 
                        bgcolor: `${colors.premiumGold}1A`, 
                        color: colors.navy,
                        width: 50, 
                        height: 50, 
                        mb: 1.5,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy, mb: 1, fontSize: '0.95rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.8rem' }}>
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Zoom>
          ))}
        </Grid>
      </Container>

      {/* Money Management Section - 4 cards in one row with Diamonds Background Image */}
      <Box 
        sx={{ 
          position: 'relative',
          py: 6,
          backgroundImage: 'url("/images/diamonds.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${colors.navy}DD 0%, ${colors.charcoal}CC 100%)`,
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Chip 
              label="MONEY MANAGEMENT" 
              sx={{ 
                bgcolor: colors.premiumGold, 
                color: colors.navy, 
                fontWeight: 700,
                px: 2,
                py: 1,
                fontSize: '0.75rem',
                letterSpacing: 1,
                mb: 2,
              }}
            />
            <Typography 
              variant="h4" 
              textAlign="center" 
              sx={{ 
                fontWeight: 700, 
                color: colors.white, 
                fontSize: { xs: '1.5rem', md: '1.8rem' }
              }}
            >
              Smart Money Management
            </Typography>
            <Typography 
              variant="body2" 
              textAlign="center" 
              sx={{ mt: 1, color: colors.white, opacity: 0.8 }}
            >
              Take control of your finances with our premium money management tools
            </Typography>
          </Box>
          
          <Grid container spacing={2.5} alignItems="stretch">
            {moneyManagementFeatures.map((feature, idx) => (
              <Zoom in timeout={300 + idx * 100} key={idx}>
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                  <Card 
                    sx={{ 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      borderTop: `2px solid ${colors.premiumGold}`,
                      bgcolor: 'rgba(26, 26, 46, 0.85)',
                      backdropFilter: 'blur(8px)',
                      '&:hover': { 
                        transform: 'translateY(-4px)', 
                        boxShadow: `0 12px 24px ${colors.premiumGold}40`,
                        '& .feature-icon': {
                          transform: 'scale(1.05)',
                          backgroundColor: colors.premiumGold,
                        }
                      } 
                    }}
                  >
                    <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Avatar 
                        className="feature-icon"
                        sx={{ 
                          bgcolor: `${colors.premiumGold}33`, 
                          color: colors.navy,
                          width: 50, 
                          height: 50, 
                          mb: 1.5,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.white, mb: 1, fontSize: '0.95rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8, lineHeight: 1.5, fontSize: '0.8rem' }}>
                        {feature.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Diamond Advantage Section */}
      <Container sx={{ py: 6 }}>
        <Fade in timeout={800}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              textAlign: 'center', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.charcoal} 100%)`,
            }}
          >
            <WorkspacePremiumIcon sx={{ fontSize: 50, color: colors.premiumGold, mb: 1.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.premiumGold, mb: 1.5, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
              Diamond Advantage
            </Typography>
            <Typography variant="body2" paragraph sx={{ maxWidth: 650, mx: 'auto', mb: 2, color: colors.white, opacity: 0.8 }}>
              We understand that sometimes you require a little extra to meet your obligations. 
              That's why we offer exclusive cash advance privileges to our valued cardholders.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip icon={<StarIcon />} label="Cash Advances" size="small" sx={{ bgcolor: colors.premiumGold, color: colors.navy, fontWeight: 600 }} />
              <Chip icon={<SpeedIcon />} label="Based on Account History" size="small" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
              <Chip icon={<CheckCircleIcon />} label="Simple Application" size="small" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
            </Box>
          </Paper>
        </Fade>
      </Container>

      {/* Purchase Protection & Mobile App - 2 Columns in one row */}
      <Container sx={{ pb: 6 }}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Zoom in timeout={500}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3, 
                bgcolor: colors.premiumGold, 
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 24px ${colors.premiumGold}40` }
              }}>
                <SecurityIcon sx={{ fontSize: 40, color: colors.navy, mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, mb: 1, fontSize: '1.2rem' }}>
                  Purchase Protection
                </Typography>
                <Typography variant="body2" sx={{ color: colors.navy, opacity: 0.8 }}>
                  PIN required for online payments with purchase protection on all purchases.
                </Typography>
              </Paper>
            </Zoom>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Zoom in timeout={500}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3, 
                bgcolor: colors.navy, 
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 24px ${colors.premiumGold}40` }
              }}>
                <PhoneAndroidIcon sx={{ fontSize: 40, color: colors.premiumGold, mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: colors.premiumGold, mb: 1, fontSize: '1.2rem' }}>
                  Mobile App
                </Typography>
                <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8 }}>
                  Check balance, view transactions, and request payments from other holders.
                </Typography>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* Signing Up Section */}
      <Box sx={{ bgcolor: colors.pearl, py: 6 }}>
        <Container>
          <Typography variant="h4" textAlign="center" sx={{ fontWeight: 700, color: colors.navy, mb: 1.5, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
            Simple Application Process
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ mb: 4, color: colors.premiumGold, fontWeight: 500 }}>
            No credit check • No minimum balance • No activation fee
          </Typography>
          
          <Stepper 
            alternativeLabel 
            activeStep={3} 
            sx={{ mb: 5, '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}
          >
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel 
                  StepIconProps={{
                    sx: {
                      '& .MuiStepIcon-root': {
                        fontSize: '1.5rem',
                        color: idx <= 3 ? colors.premiumGold : '#ccc',
                        '&.Mui-active': { color: colors.premiumGold },
                        '&.Mui-completed': { color: colors.premiumGold },
                      }
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              textAlign: 'center', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.charcoal} 100%)`,
            }}
          >
            <Typography variant="h5" sx={{ color: colors.premiumGold, fontWeight: 700, mb: 1.5 }}>
              Applying takes just 10 minutes!
            </Typography>
            <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8, mb: 2 }}>
              Join the exclusive Diamond Card community and experience banking redefined
            </Typography>
            <Button 
              variant="contained" 
              size="medium" 
              sx={{ 
                bgcolor: colors.premiumGold,
                color: colors.navy,
                fontWeight: 700, 
                px: 4, 
                py: 1,
                fontSize: '0.85rem',
                borderRadius: 50,
                '&:hover': { bgcolor: colors.lightGold, transform: 'translateY(-2px)' },
              }}
            >
              Order your Diamond Card now — receive it within 14 business days
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Trust Badges */}
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: colors.textLight }}>
          Trusted by thousands of premium cardholders worldwide
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
          <Chip label="★★★★★ 4.9 Rating" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
          <Chip label="PCI Compliant" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
          <Chip label="FDIC Insured" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
          <Chip label="24/7 Support" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
        </Box>
      </Container>
    </Box>
  );
};

export default ProductDebit;